import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplier } from '../queryKeys';

import { fetchSupplierDetail } from './fetchSupplierDetail';
import { type SupplierDetailResponse } from './types';

/**
 * Generates the query options for the supplier detail query so they can be
 * shared between the useQuery hook and prefetching in route loaders.
 *
 * @param deId - The data exchange ID of the supplier relationship.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and staleTime.
 */
export const getSupplierDetailQueryOptions = <TData = SupplierDetailResponse>({
  deId,
  options,
}: {
  deId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<SupplierDetailResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: deId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchSupplierDetail(deId, { signal })
      : skipToken,
    queryKey: queryKeysSupplier.getDetail(deId ?? ''),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });

/**
 * React Query based custom hook for fetching the detail view of a single
 * supplier relationship.
 *
 * @param deId - The data exchange ID of the supplier relationship.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook containing the supplier detail.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/get_supplier_detail_suppliers__de_id__get
 */
export const useQuerySupplierDetail = <TData = SupplierDetailResponse>(
  deId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<SupplierDetailResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getSupplierDetailQueryOptions<TData>({ deId, options }));
